/**
 * «Документы → Реквизиты»: кто выставляет документы.
 *
 * Реквизиты исполнителя, его подпись и печать уходят в каждый счёт, акт,
 * наряд и прайс — поэтому живут рядом с документами, а не в «Настройках».
 *
 * Вкладка открывается ПРОСМОТРОМ: реквизиты сверяют с бумагой чаще, чем
 * правят, а два десятка полей ввода на экране приглашают исправить лишнее.
 * Правка включается кнопкой «Изменить» и доступна только владельцу — это же
 * проверяет сервер.
 */

import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, ImagePlus, Pencil, X } from 'lucide-react';
import { useBootstrap, useFacsimile, useMe } from '../api/queries.js';
import { useDrafts } from '../store/drafts.js';
import { useModule } from '../store/useModule.js';
import { Button, Field, Spinner } from '../components/ui.js';

type Show = (text: string, tone?: 'info' | 'error') => void;

const PERFORMER_FIELDS: { key: string; label: string; wide?: boolean }[] = [
  { key: 'name', label: 'Полное наименование', wide: true },
  { key: 'shortName', label: 'Краткое — для документов' },
  { key: 'ogrnip', label: 'ОГРНИП' },
  { key: 'inn', label: 'ИНН' },
  { key: 'address', label: 'Адрес', wide: true },
  { key: 'phone', label: 'Телефон' },
  { key: 'bank', label: 'Банк' },
  { key: 'bik', label: 'БИК' },
  { key: 'rs', label: 'Расчётный счёт' },
  { key: 'ks', label: 'Корр. счёт' },
  { key: 'signer', label: 'Подписант' },
];

const CARD = { borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' };

export function RequisitesTab({ show }: { show: Show }) {
  const me = useMe();
  const bootstrap = useBootstrap(true);
  const module = useModule('settings');
  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const [saving, setSaving] = useState(false);

  const isOwner = me.data?.user.role === 'OWNER';
  const settings = bootstrap.data?.settings;

  if (bootstrap.isLoading || !settings) return <Spinner />;

  const saved = (settings.performer as Record<string, string> | null) ?? {};
  const current = draft ?? saved;
  const editing = draft !== null;

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    // Настройки — синглтон: черновик заводится под фиксированным id = 1.
    useDrafts.getState().load('settings', 'settings', [{ id: '1', version: settings.version }]);
    useDrafts.getState().update('settings', 'settings', '1', { performer: draft });
    const outcome = await module.save();
    setSaving(false);
    if (outcome.ok) {
      setDraft(null);
      void bootstrap.refetch();
    } else {
      show(outcome.error ?? 'Не удалось сохранить', 'error');
    }
  };

  return (
    <div className="max-w-4xl space-y-4">
      <section className="rounded-xl border p-4" style={CARD}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-medium text-ink">Реквизиты исполнителя</p>
          {isOwner &&
            (editing ? (
              <span className="flex gap-2">
                <Button onClick={() => void save()} disabled={saving}>
                  <Check size={14} /> {saving ? 'Сохранение…' : 'Сохранить'}
                </Button>
                <Button
                  variant="secondary"
                  disabled={saving}
                  onClick={() => {
                    module.discard();
                    setDraft(null);
                  }}
                >
                  <X size={14} /> Отменить
                </Button>
              </span>
            ) : (
              <Button variant="secondary" onClick={() => setDraft({ ...saved })}>
                <Pencil size={14} /> Изменить
              </Button>
            ))}
        </div>

        {/*
          В просмотре — строки, а не серые поля ввода: так лист читается
          глазами, как справка, и видно, где значение не заполнено.
        */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {PERFORMER_FIELDS.map((field) => (
            <div key={field.key} className={field.wide ? 'sm:col-span-2' : ''}>
              {editing ? (
                <Field label={field.label}>
                  <input
                    className="otc-input"
                    value={current[field.key] ?? ''}
                    onChange={(e) => setDraft({ ...current, [field.key]: e.target.value })}
                  />
                </Field>
              ) : (
                <div>
                  <p className="text-xs text-ink2">{field.label}</p>
                  <p className="text-sm text-ink">
                    {current[field.key]?.trim() || <span className="text-ink2">не заполнено</span>}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <FacsimileSection isOwner={isOwner} show={show} />
    </div>
  );
}

/**
 * Подпись и печать.
 *
 * Лежат файлами на сервере: их ставят в документы галочкой «Подпись и печать»,
 * а здесь показывают и заменяют — скан подписи меняется раз в годы, но, когда
 * это случается, лезть к разработчику незачем.
 */
function FacsimileSection({ isOwner, show }: { isOwner: boolean; show: Show }) {
  const facsimile = useFacsimile();
  const queryClient = useQueryClient();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['facsimile'] });

  return (
    <section className="rounded-xl border p-4" style={CARD}>
      <p className="mb-3 text-sm font-medium text-ink">Подпись и печать</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <FacsimileCard kind="signature" title="Подпись" width={600} current={facsimile?.signature ?? null} isOwner={isOwner} show={show} onUploaded={refresh} />
        <FacsimileCard kind="stamp" title="Печать" width={480} current={facsimile?.stamp ?? null} isOwner={isOwner} show={show} onUploaded={refresh} />
      </div>
    </section>
  );
}

function FacsimileCard({
  kind,
  title,
  width,
  current,
  isOwner,
  show,
  onUploaded,
}: {
  kind: 'signature' | 'stamp';
  title: string;
  /** Ширина итоговой картинки: около 300 точек на дюйм при печати. */
  width: number;
  current: string | null;
  isOwner: boolean;
  show: Show;
  onUploaded: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [prepared, setPrepared] = useState<{ blob: Blob; url: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const pick = async (file: File) => {
    try {
      const blob = await prepareFacsimile(file, width);
      setPrepared({ blob, url: URL.createObjectURL(blob) });
    } catch (error) {
      show(error instanceof Error ? error.message : 'Не удалось прочитать картинку', 'error');
    }
  };

  const cancel = () => {
    if (prepared) URL.revokeObjectURL(prepared.url);
    setPrepared(null);
  };

  const upload = async () => {
    if (!prepared) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', prepared.blob, `${kind}.png`);
      const response = await fetch(`/api/facsimile/${kind}`, { method: 'POST', body: form, credentials: 'same-origin' });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? 'Не удалось сохранить');
      }
      show(`${title} заменена`);
      cancel();
      onUploaded();
    } catch (error) {
      show(error instanceof Error ? error.message : 'Не удалось сохранить', 'error');
    } finally {
      setBusy(false);
    }
  };

  const shown = prepared?.url ?? current;

  return (
    <div className="rounded-lg border p-3" style={{ borderColor: 'var(--otc-line)' }}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-sm text-ink">{title}</p>
        {prepared && (
          <span className="text-[11px]" style={{ color: 'var(--otc-amber)' }}>
            новая — ещё не сохранена
          </span>
        )}
      </div>

      {/* Белая подложка — как лист: так видно, чисто ли убран фон. */}
      <div
        className="flex h-40 items-center justify-center rounded-md border"
        style={{ borderColor: 'var(--otc-line)', background: '#fff' }}
      >
        {shown ? (
          <img src={shown} alt={title} className="max-h-36 max-w-full object-contain" />
        ) : (
          <span className="text-xs text-ink2">не загружена</span>
        )}
      </div>

      {isOwner && (
        <div className="mt-2 flex flex-wrap gap-2">
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void pick(file);
            }}
          />
          {prepared ? (
            <>
              <Button onClick={() => void upload()} disabled={busy}>
                {busy ? 'Сохранение…' : 'Сохранить'}
              </Button>
              <Button variant="secondary" onClick={cancel} disabled={busy}>
                Отмена
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={() => input.current?.click()}>
              <ImagePlus size={14} /> {current ? 'Заменить' : 'Загрузить'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Скан или фото → PNG с прозрачным фоном, обрезанный по чернилам.
 *
 * Светлое (бумага) становится прозрачным, тёмное (чернила) остаётся своего
 * цвета. Края считаются по строкам и столбцам, где чернил хотя бы несколько
 * точек: одиночная пылинка на скане иначе растянула бы рамку на весь лист.
 */
async function prepareFacsimile(file: File, width: number): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();

    // Крупный скан сначала уменьшается: миллионы точек обрабатывались бы секундами.
    const scale = Math.min(1, 2400 / Math.max(img.width, img.height));
    const W = Math.max(1, Math.round(img.width * scale));
    const H = Math.max(1, Math.round(img.height * scale));
    const source = document.createElement('canvas');
    source.width = W;
    source.height = H;
    const g = source.getContext('2d')!;
    g.drawImage(img, 0, 0, W, H);

    const pixels = g.getImageData(0, 0, W, H);
    const p = pixels.data;
    const columns = new Uint32Array(W);
    const rows = new Uint32Array(H);

    for (let i = 0; i < p.length; i += 4) {
      const lum = 0.299 * p[i]! + 0.587 * p[i + 1]! + 0.114 * p[i + 2]!;
      const a = Math.max(0, Math.min(1, (235 - lum) / 110)) * (p[i + 3]! / 255);
      // Цвет чернил без примеси бумаги — иначе края подписи выходят бледными.
      if (a > 0) {
        for (let c = 0; c < 3; c++) p[i + c] = Math.max(0, Math.min(255, Math.round((p[i + c]! - 255 * (1 - a)) / a)));
      }
      p[i + 3] = Math.round(a * 255);
      if (a > 0.3) {
        const k = i / 4;
        const x = k % W;
        columns[x]! += 1;
        rows[(k - x) / W]! += 1;
      }
    }

    const MIN = 3;
    const first = (counts: Uint32Array) => counts.findIndex((n) => n >= MIN);
    const last = (counts: Uint32Array) => {
      for (let i = counts.length - 1; i >= 0; i--) if (counts[i]! >= MIN) return i;
      return -1;
    };
    let minX = first(columns);
    let maxX = last(columns);
    let minY = first(rows);
    let maxY = last(rows);
    if (minX < 0 || minY < 0) {
      throw new Error('На картинке не видно ни подписи, ни печати — нужен скан или фото на белой бумаге');
    }
    g.putImageData(pixels, 0, 0);

    const pad = 8;
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(W - 1, maxX + pad);
    maxY = Math.min(H - 1, maxY + pad);
    const cw = maxX - minX + 1;
    const ch = maxY - minY + 1;

    const out = document.createElement('canvas');
    out.width = Math.min(width, cw);
    out.height = Math.max(1, Math.round((ch / cw) * out.width));
    const o = out.getContext('2d')!;
    o.imageSmoothingQuality = 'high';
    o.drawImage(source, minX, minY, cw, ch, 0, 0, out.width, out.height);

    return await new Promise<Blob>((resolve, reject) =>
      out.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Не удалось подготовить картинку'))), 'image/png'),
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}
